import { I18nLocale } from "@/i18nConfig.server";
import { flattenDepth, isArray, isObject, map, remove } from "lodash";
import qs from "qs";
import { apiPath } from "@/cms-helpers/api-constant.client";

const comparisonOperators = [
  "$eq", // Equal
  "$eqi", // Equal (case-insensitive)
  "$ne", // Not equal
  "$nei", // Not equal (case-insensitive)
  "$lt", // Less than
  "$lte", // Less than or equal to
  "$gt", // Greater than
  "$gte", // Greater than or equal to
] as const;

export type ComparisonOperator = (typeof comparisonOperators)[number];

const nullCheckOperators = [
  "$null", // Is null
  "$notNull", // Is not null
] as const;

export type NullCheckOperator = (typeof nullCheckOperators)[number];

const rangeOperators = [
  "$between", // Is between
] as const;

export type RangeOperator = (typeof rangeOperators)[number];

const arrayOperators = [
  "$in", // Included in an array
  "$notIn", // Not included in an array
] as const;

export type ArrayOperator = (typeof arrayOperators)[number];

const compositeOperators = [
  "$or", // Joins the filters in an "or" expression
  "$and", // Joins the filters in an "and" expression
  "$not", // Joins the filters in a "not" expression
] as const;

export type CompositeOperator = (typeof compositeOperators)[number];

const stringOperators = [
  "$contains", // Contains
  "$notContains", // Does not contain
  "$containsi", // Contains (case-insensitive)
  "$notContainsi", // Does not contain (case-insensitive)
  "$startWith", // Starts with
  "$startWithi", // Starts with (case-insensitive)
  "$endWith", // Ends with
  "$endWithi", // Ends with (case-insensitive)
] as const;

export type StringOperator = (typeof stringOperators)[number];

export type FilterOperator =
  | ComparisonOperator
  | ArrayOperator
  | CompositeOperator
  | StringOperator
  | NullCheckOperator
  | RangeOperator;

export type CMSFilterQueryType =
  | {
      [K in FilterOperator]?: K extends StringOperator
        ? string | number
        : K extends ComparisonOperator
          ? string | number
          : K extends NullCheckOperator
            ? boolean
            : K extends RangeOperator
              ? [string | number, string | number]
              : K extends ArrayOperator
                ? (string | number)[]
                : K extends CompositeOperator
                  ? CMSFilterQueryType[]
                  : CMSFilterQueryType;
    }
  | {
      [K: string]: CMSFilterQueryType;
    };

export interface CMSQueryParamBuilder {
  populate?:
    | "*"
    | string
    | string[]
    | Record<string, Omit<CMSQueryParamBuilder, "pagination"> | boolean>;
  fields?: string[];
  filters?: CMSFilterQueryType;
  locale?: I18nLocale;
  sort?: string | string[];
  limit?: number;
  pagination?:
    | {
        page?: number;
        pageSize?: number;
        withCount?: boolean;
      }
    | {
        start?: number;
        limit?: number;
        withCount?: boolean;
      };
  publicationState?: "live" | "preview";

  [key: string]: any;
}

export const cmsQueryParamBuilder = (
  obj: CMSQueryParamBuilder = {},
  options?: qs.IStringifyOptions,
) => {
  return qs.stringify(obj, { encodeValuesOnly: true, ...options });
};

export const generateQueryParams = (
  params: (string | CMSQueryParamBuilder | CMSQueryParamBuilder[])[],
  addQueryPrefix: boolean = true,
): string => {
  if (!params.length) {
    return "";
  }

  remove(params, (param) => !param);

  const queryParamArray = params.map((param) => {
    if (typeof param === "string") {
      return param;
    }
    if (isArray(param)) {
      return map(param || [], (value) => {
        if (isObject(value) && !isArray(value)) {
          return cmsQueryParamBuilder(value);
        } else {
          return generateQueryParams([value], false);
        }
      });
    }
    return cmsQueryParamBuilder(param);
  });

  const queryParams = flattenDepth(queryParamArray, 10).join("&").trim();

  return `${addQueryPrefix ? "?" : ""}${queryParams}`.trim();
};

export interface CreateUrlWithApi {
  basePath?: string;
  path: string | string[];
  query?: CMSQueryParamBuilder;
  options?: qs.IStringifyOptions;
}

export const createUrlWithApiClient = (params: CreateUrlWithApi) => {
  const { basePath = apiPath, path, query, options } = params;
  if (!path) {
    throw new Error("Path is required");
  }
  const pathArray = isArray(path) ? path : [path];
  const paths = map(pathArray, (path) => path.replace(/^\//, "").trim()).join(
    "/",
  );

  const routeItems = [
    basePath,
    paths,
    cmsQueryParamBuilder(query, { addQueryPrefix: true, ...options }),
  ];

  return routeItems.join("/");
};

export const mediaPopulationFields = [
  "name",
  "alternativeText",
  "caption",
  "url",
  "width",
  "height",
  "ext",
  "mime",
];
